import * as React from 'react';
import { SubmissionPublicFieldFragment } from '@/graphql/fragment/submission.public.fragment';
import type { SvgIconComponent } from '@mui/icons-material';

import AccountBalance from '@mui/icons-material/AccountBalance';
import Biotech from '@mui/icons-material/Biotech';
import Bolt from '@mui/icons-material/Bolt';
import BusinessCenter from '@mui/icons-material/BusinessCenter';
import Category from '@mui/icons-material/Category';
import CellTower from '@mui/icons-material/CellTower';
import DirectionsCar from '@mui/icons-material/DirectionsCar';
import FlightTakeoff from '@mui/icons-material/FlightTakeoff';
import Hotel from '@mui/icons-material/Hotel';
import LocalGroceryStore from '@mui/icons-material/LocalGroceryStore';
import LocalHospital from '@mui/icons-material/LocalHospital';
import LocalShipping from '@mui/icons-material/LocalShipping';
import Memory from '@mui/icons-material/Memory';
import Movie from '@mui/icons-material/Movie';
import School from '@mui/icons-material/School';
import SportsSoccer from '@mui/icons-material/SportsSoccer';
import Storefront from '@mui/icons-material/Storefront';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import type { SvgIconProps } from '@mui/material/SvgIcon';

// consts
export const DATA_PERMISSION_ID = 'J7vPOV';
export const LOADING_TEXT = 'Please wait... Your results are being calculated.';
export const QUESTION = 'Question';
export const GUIDANCE_TEXT = 'Select one answer';
export const BUTTON_TEXT = 'continue';
export const START_INNOVATION_MODEL =
  'GEO readiness is the ability to influence how generative engines understand, select, and cite your brand—so you show up accurately and competitively in AI-driven discovery.';
export const START_LEFT_CONTEXT =
  'Search and discovery are being reshaped in real time—driven by generative AI, changing consumer behavior, and the rapid rise of AI answers inside search experiences. As this shift accelerates, brands are quickly realizing that ranking in traditional search engines is no longer enough; they must become credible, citable, and consistently represented across LLMs.';
export const START_RIGHT_CONTEXT =
  'We’ve identified the core capabilities that differentiate organizations that are GEO-Ready from those at risk. Take this brief assessment to estimate the potential revenue at risk from inaction—so you can prioritize the right strategies and investments.';
export const defaults = {
  '1': {
    // Automotive
    AO: 0.14,
    Z: 0.6,
    RR: 0.12,
    BCS: 0.06, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.3,
    NGD: 0.004,
    CRR: 0.132,
    LRR: 0.156,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '2': {
    // Consumer Goods
    AO: 0.1,
    Z: 0.5,
    RR: 0.08,
    BCS: 0.05, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.2,
    NGD: 0.004,
    CRR: 0.088,
    LRR: 0.104,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '3': {
    // Energy & Utilities // This is off on the dashboard_instrustry but calculations are correct
    AO: 0.08,
    Z: 0.6,
    RR: 0.08,
    BCS: 0.06, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.15,
    NGD: 0.004,
    CRR: 0.088,
    LRR: 0.104,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '4': {
    // Healthcare
    AO: 0.22,
    Z: 0.65,
    RR: 0.12,
    BCS: 0.078, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.24,
    BSRD: 0.2,
    NGD: 0.006,
    CRR: 0.132,
    LRR: 0.156,
    headroom: [0.12, 0.012, 0.078, 0.048, 0.024],
  },
  '5': {
    // Insurance
    AO: 0.15,
    Z: 0.65,
    RR: 0.1,
    BCS: 0.065, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.5,
    NGD: 0.005,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '6': {
    // Professional Services
    AO: 0.08,
    Z: 0.6,
    RR: 0.1,
    BCS: 0.06, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.2,
    NGD: 0.004,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '7': {
    // Recreation & Sports
    AO: 0.08,
    Z: 0.6,
    RR: 0.08,
    BCS: 0.08, // Need to figure out how this on is calculated
    DCS: 0.08,
    LDCS: 0.16,
    BSRD: 0.3,
    NGD: 0.004,
    CRR: 0.088,
    LRR: 0.104,
    headroom: [0.08, 0.068, 0.052, 0.032, 0.016],
  },
  '8': {
    // Telecommunications
    AO: 0.12,
    Z: 0.6,
    RR: 0.1,
    BCS: 0.06, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.4,
    NGD: 0.005,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '9': {
    // Travel
    AO: 0.14,
    Z: 0.55,
    RR: 0.12,
    BCS: 0.055, // Need to figure out how this on is calculated
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.6,
    NGD: 0.005,
    CRR: 0.132,
    LRR: 0.156,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '10': {
    // Financial Services
    AO: 0.15,
    Z: 0.65,
    RR: 0.1,
    BCS: 0.065,
    DCS: 0.1,
    LDCS: 0.13,
    BSRD: 0.4,
    NGD: 0.005,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '11': {
    // Education
    AO: 0.12,
    Z: 0.6,
    RR: 0.12,
    BCS: 0.06,
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.4,
    NGD: 0.005,
    CRR: 0.132,
    LRR: 0.156,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '12': {
    // Entertainment Media
    AO: 0.12,
    Z: 0.7,
    RR: 0.04,
    BCS: 0.07,
    DCS: 0.08,
    LDCS: 0.16,
    BSRD: 0.5,
    NGD: 0.008,
    CRR: 0.044,
    LRR: 0.052,
    headroom: [0.08, 0.068, 0.052, 0.032, 0.016],
  },
  '13': {
    // Hospitality
    AO: 0.12,
    Z: 0.55,
    RR: 0.1,
    BCS: 0.055,
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.55,
    NGD: 0.008,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '14': {
    // Life Sciences
    AO: 0.2,
    Z: 0.65,
    RR: 0.12,
    BCS: 0.065,
    DCS: 0.12,
    LDCS: 0.24,
    BSRD: 0.15,
    NGD: 0.006,
    CRR: 0.132,
    LRR: 0.156,
    headroom: [0.12, 0.012, 0.078, 0.048, 0.024],
  },
  '15': {
    // Retail
    AO: 0.1,
    Z: 0.5,
    RR: 0.14,
    BCS: 0.05,
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.6,
    NGD: 0.004,
    CRR: 0.154,
    LRR: 0.182,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '16': {
    // Technology
    AO: 0.16,
    Z: 0.6,
    RR: 0.16,
    BCS: 0.06,
    DCS: 0.12,
    LDCS: 0.24,
    BSRD: 0.7,
    NGD: 0.005,
    CRR: 0.176,
    LRR: 0.208,
    headroom: [0.12, 0.012, 0.078, 0.048, 0.024],
  },
  '17': {
    // Transportation
    AO: 0.08,
    Z: 0.6,
    RR: 0.1,
    BCS: 0.078,
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.2,
    NGD: 0.004,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
  '18': {
    // Other
    AO: 0.1,
    Z: 0.6,
    RR: 0.1,
    BCS: 0.06,
    DCS: 0.1,
    LDCS: 0.2,
    BSRD: 0.4,
    NGD: 0.005,
    CRR: 0.11,
    LRR: 0.13,
    headroom: [0.1, 0.085, 0.065, 0.04, 0.02],
  },
} as const;

export const industrySnippets = {
  '1': [
    'Search strongly influences model comparison and maintenance research, but many purchases still close offline or through dealer channels.',
    'Comparison-heavy and "which car should I buy" queries are increasingly summarized directly in AI answer panels.',
    'Early‑funnel research is often satisfiable in‑pane before shoppers visit OEM or dealer sites.',
    'Shoppers commonly return after AI exposure to configure pricing, locate inventory, or validate trust signals.',
    'Solid technical hygiene plus authoritative specs and FAQs can materially increase how often the brand gets cited.',
    'Citation share is constrained because AI answers typically cite multiple OEMs, publishers, and forums in the category.',
    'Without an active program, competitors and aggregators gradually displace brands in AI-summarized research journeys.',
  ],
  '2': [
    'Search drives discovery and product education, but revenue frequently routes through retailers and marketplaces rather than brand-owned eCommerce.',
    'How‑to, ingredient, and comparison queries around categories are increasingly answered in AI panels before users click through.',
    'Many users still need product pages, reviews, and availability information—keeping downstream site visits meaningful.',
    'Follow‑up visits depend heavily on brand trust and availability—AI exposure can lift consideration but not guarantee purchase.',
    'Foundational GEO work improves inclusion on comparison questions and drives cited traffic into PDPs and category hubs.',
    'Citation capture is limited by retailer dominance and aggregator content in many consumer categories.',
    'Inaction slowly shifts AI citations toward retailers, publishers, and competitors who invest in structured content.',
  ],
  '3': [
    'Many journeys are research-heavy and local; users often seek rates, programs, and outage/service info before taking action.',
    'Definitions, billing explanations, and program comparisons are increasingly satisfied in AI summaries with fewer click-throughs.',
    "If utilities don't publish clear explainers and tables, third parties may become the default sources in AI answers.",
    'Users still return for account actions, sign-ups, and program enrollment—AI can act as a top-of-funnel filter.',
    'Clear structured program pages, FAQs, and schema improve discoverability and citation likelihood in AI panels.',
    'Citation share may be constrained by regulators, government sites, and consumer advocacy sources appearing alongside utilities.',
    'Without active governance, outdated program info and third-party summaries can dominate AI-surfaced answers over time.',
  ],
  '4': [
    'Informational care queries trigger AI answers; trust, quality, and authority signals strongly shape which sources get cited.',
    'Symptoms, conditions, and treatment comparisons are increasingly summarized in AI panels before users click.',
    'If provider content is thin, AI surfaces may route patients to large publishers or health portals instead of local systems.',
    'Users still visit to schedule, confirm in-network coverage, and evaluate providers—making conversion pathways critical.',
    'Expert-led FAQs, condition hubs, and clinician bios improve authority and citation inclusion for high-intent queries.',
    'Citation share is constrained by large health publishers and government sources that frequently appear in AI answers.',
    'Without maintenance, outdated clinical content and weaker E-E-A-T signals reduce AI visibility on key topics.',
  ],
  '5': [
    'Insurance research is complex; users rely on search for definitions and comparisons before engaging agents or portals.',
    'Policy, coverage, and claim questions are increasingly summarized directly in AI answer experiences.',
    'AI panels can reduce clicks for basic definitions, shifting value to calculators, eligibility tools, and next-step guidance.',
    'Users commonly return to quote, bind, or file claims—AI exposure can influence consideration but not replace workflow tasks.',
    'Compliant explainers, coverage tables, and calculators boost inclusion and drive qualified traffic to quote flows.',
    'Citation share is limited by regulators, consumer advocacy sites, and major publishers frequently cited in insurance topics.',
    'Without ongoing monitoring, AI citations can drift toward aggregators and comparison sites over brand-owned content.',
  ],
  '6': [
    'Professional services buyers heavily research solutions and providers online before contacting firms.',
    '"Best firm," "how to," and comparison queries are increasingly answered in AI summaries, compressing early funnel traffic.',
    "If firms don't publish specific proof points, AI may default to directories, analysts, and publishers as sources.",
    'Users still return for credibility signals—case studies, expert bios, and contact pathways become the conversion fulcrum.',
    'Evidence-rich thought leadership, clear services pages, and strong entity signals raise inclusion in AI citations.',
    'Citation share can be constrained by directory/analyst ecosystems and large publishers that AI often references.',
    'Inaction allows competitors with clearer positioning and structured content to gain AI visibility over time.',
  ],
  '7': [
    'Sports and recreation discovery is search-led, but many conversions happen through ticketing platforms, memberships, or local venues.',
    'Event, gear, and "what to do" queries are increasingly summarized in AI panels, especially for planning questions.',
    "If brands don't provide structured schedules and guides, AI may cite aggregators and community sources instead.",
    'Users still return for availability, booking, and purchase actions—AI exposure can influence choice and timing.',
    'Structured listings, guides, FAQs, and clear offers improve citation likelihood and downstream conversions.',
    'Citation share is limited by major publishers, review sites, and marketplaces frequently included in AI answers.',
    'Without always-on updates, stale listings and weaker content cadence reduce AI inclusion and cited traffic.',
  ],
  '8': [
    'Telecom shoppers heavily compare plans, coverage, and devices via search before purchasing online or in-store.',
    'Plan comparisons and "best internet/mobile" queries are increasingly summarized in AI answer panels.',
    "If carriers don't publish clear tables and eligibility details, AI may cite third-party comparison sites instead.",
    'Users still return to check availability, pricing, promos, and to start checkout—making mid-funnel assets critical.',
    'Plan pages with structured pricing, coverage maps, and FAQs improve AI inclusion and citations.',
    'Citation share is constrained by comparison publishers and review sites that AI often references for telecom shopping.',
    'Without governance, citations can drift toward aggregators and competitors with clearer structured plan content.',
  ],
  '9': [
    'Travel planning is research-heavy; users search destinations, itineraries, and booking options before converting.',
    '"Where to go," comparison, and itinerary queries are increasingly answered in AI summaries, reducing early clicks.',
    'If travel brands lack unique guides and data, AI may default to publishers, OTAs, and community sources as citations.',
    'Users still return for booking, availability, pricing, and policies—AI can shape choice but not replace booking flows.',
    'Destination hubs, structured itineraries, and policy pages improve citations and qualified traffic to booking steps.',
    'Citation share is limited by OTAs, major publishers, and forums commonly referenced in travel topics.',
    'Without monitoring, AI answers can skew toward third-party listings and away from brand-owned travel content.',
  ],
  '10': [
    'Finance research includes definitions and comparisons; users often start with search but take action within trusted platforms.',
    'Advice/definitions are AI‑summarized; trust/regulatory sources heavily influence what gets cited.',
    'Basic queries may end in AI panels unless brands offer calculators, tables, and compliant next-step guidance.',
    'Users still return for account actions, applications, and verification steps—conversion journeys remain site/app-centric.',
    'Compliant explainers, calculators, rate tables, and schema improve AI inclusion and downstream intent.',
    'Citation share is constrained by regulators, major publishers, and aggregators that AI frequently cites in finance topics.',
    'Without active management, AI citations drift toward third-party explainers and comparison sources over time.',
  ],
  '11': [
    'Education decisions involve long research cycles; search drives discovery, comparisons, and program exploration.',
    'Program, admissions, and "best school" queries are increasingly summarized in AI answers before users click.',
    "If institutions don't provide clear program details and outcomes, AI may cite directories and publishers instead.",
    'Users still return for applications, requirements, and financial aid—making task completion pathways essential.',
    'Structured program pages, outcomes data, FAQs, and strong entity signals increase AI inclusion and citations.',
    'Citation share is constrained by rankings, directories, and government sources often included in education answers.',
    'Without ongoing updates, AI can surface outdated admissions and program info, reducing visibility and trust.',
  ],
  '12': [
    'Entertainment discovery is search-led, but consumption often happens inside platforms; visibility shapes awareness and subscription intent.',
    'Reviews, "what to watch," and comparison queries are increasingly summarized in AI panels, compressing publisher traffic.',
    "If brands don't provide structured catalogs and unique editorial hooks, AI may cite aggregators and publishers instead.",
    'Users still return for trailers, availability, and subscription steps—AI can influence selection but not replace viewing platforms.',
    'Structured show/movie pages, metadata, and evergreen guides improve citations and drive downstream conversion actions.',
    'Citation share is limited by major publishers and databases that AI frequently references for entertainment topics.',
    'Without content cadence and metadata hygiene, AI visibility can shift toward competitors and third-party sources.',
  ],
  '13': [
    'Hospitality searches are intent-rich; users compare options and policies before booking through brand sites or OTAs.',
    'Hotel comparisons, "best near me," and policy questions are increasingly summarized in AI experiences.',
    "If brands don't publish clear amenities and policy details, AI may cite OTAs and review sites as primary sources.",
    'Users still return for availability, pricing, and booking—AI exposure can shape consideration and channel preference.',
    'Location pages, structured amenities, FAQs, and policy pages improve AI inclusion and cited traffic quality.',
    'Citation share is constrained by OTAs, review platforms, and publishers commonly cited in hospitality answers.',
    'Without monitoring, AI citations can drift toward intermediaries, reducing direct booking influence over time.',
  ],
  '14': [
    'Life sciences audiences seek authoritative information; search drives discovery but credibility and compliance dominate.',
    'Condition, mechanism, and therapy comparisons are increasingly summarized in AI panels with limited click-throughs.',
    "If companies don't publish clear, compliant explainers, AI may default to journals, government, and major publishers.",
    'Users still return for clinical trial info, HCP resources, and patient support pathways—task flows matter.',
    'Evidence-backed content, structured data, and source transparency improve AI inclusion and citation likelihood.',
    'Citation share is constrained by journals, regulators, and health publishers that AI often prioritizes as sources.',
    'Without governance, outdated scientific content and weak entity signals reduce AI visibility on priority topics.',
  ],
  '15': [
    'Retail shopping journeys start with search and comparison, but purchase paths vary across brand sites and marketplaces.',
    'Product comparison and "what to buy" queries frequently trigger AI summaries before users click.',
    'If retailers lack authoritative guides and reviews, AI may cite publishers and aggregators instead of owned content.',
    'Users still return for pricing, inventory, shipping, and checkout—AI can influence but not replace commerce tasks.',
    'Buying guides, UGC/reviews, and structured PDP content improve citations and drive qualified traffic.',
    'Citation share is constrained by large marketplaces and publishers frequently referenced in retail topics.',
    'Without active optimization, AI visibility and citations shift toward competitors and third-party comparison content.',
  ],
  '16': [
    'Technology buyers research "best tools" and implementation details; search heavily influences shortlists and vendor selection.',
    '"Best tools," "how‑to," and implementation queries are increasingly summarized directly in AI answer panels.',
    "If vendors don't publish clear comparisons and proof points, AI may cite analysts, forums, and publishers instead.",
    'Users still return for demos, documentation, and trust signals—conversion depends on evidence and enablement content.',
    'Comparison pages, customer evidence, implementation guides, and strong entity signals improve AI inclusion and citations.',
    'Citation share is constrained by analysts, communities, and major publishers often included in tech AI answers.',
    'Without continuous updates, AI citations drift toward competitors with fresher docs and clearer structured content.',
  ],
  '17': [
    'Transportation decisions often involve logistics and compliance research; search supports vendor selection and policy understanding.',
    'Service, cost, and "best option" comparison queries are increasingly summarized in AI panels.',
    "If providers don't publish clear service definitions and calculators, AI may cite aggregators and publishers instead.",
    'Users still return for quotes, tracking, and booking—AI shapes research but workflows still drive site/app visits.',
    'Service hubs, calculators, structured FAQs, and proof points increase AI inclusion and citation likelihood.',
    'Citation share is limited by marketplaces, directories, and government sources often referenced in transportation topics.',
    'Without monitoring, AI citations can drift toward intermediaries, reducing direct influence over time.',
  ],
  '18': [
    '"Other" categories vary, but discovery typically begins with search and is increasingly mediated by AI summaries.',
    'General definitions and comparisons are often satisfied in AI answer panels before users click.',
    "If brands don't provide structured, authoritative content, AI will cite the clearest third-party sources available.",
    'Users still return for transactions and task completion—conversion depends on strong next-step pathways.',
    'Foundational technical hygiene plus structured FAQs and clear offers improve AI inclusion and citations.',
    'Citation share is constrained by dominant publishers and aggregators that AI frequently references across topics.',
    'Without an active program, AI visibility gradually shifts toward competitors and third-party content over time.',
  ],
} as const;

export const INDUSTRY_ICONS: Record<string, SvgIconComponent> = {
  '1': DirectionsCar,
  '2': LocalGroceryStore,
  '3': Bolt,
  '4': LocalHospital,
  '5': VerifiedUser,
  '6': BusinessCenter,
  '7': SportsSoccer,
  '8': CellTower,
  '9': FlightTakeoff,
  '10': AccountBalance,
  '11': School,
  '12': Movie,
  '13': Hotel,
  '14': Biotech,
  '15': Storefront,
  '16': Memory,
  '17': LocalShipping,
  '18': Category,
};

const INDUSTRY_NAMES_BY_ID: Record<string, string> = {
  '1': 'automotive',
  '2': 'consumer goods',
  '3': 'energy & utilities',
  '4': 'healthcare',
  '5': 'insurance',
  '6': 'professional services',
  '7': 'recreation & sports',
  '8': 'telecommunications',
  '9': 'travel',
  '10': 'financial services',
  '11': 'education',
  '12': 'entertainment, media',
  '13': 'hospitality',
  '14': 'life sciences',
  '15': 'retail',
  '16': 'technology',
  '17': 'transportation',
  '18': 'other',
};

type IndustryDefaults = typeof defaults;
type IndustryKey = keyof IndustryDefaults;

type Props = { industryId: string; label?: string };

// helper functions
function removeQuotes(value: string): string {
  return value.replace(/"/g, '');
}

function normalizeIndustry(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ');
}

export function getIndustryIcon(industry: string): SvgIconComponent {
  const key = normalizeIndustry(industry);
  return INDUSTRY_ICONS[key] ?? Category;
}

export function getIndustryNameFromId(industryId: string): string {
  const key = industryId.trim();
  return INDUSTRY_NAMES_BY_ID[key] ?? 'other';
}

export function getBCS(
  surveyPercentage: number,
  options: readonly number[]
): number {
  if (surveyPercentage >= 0 && surveyPercentage < 25) {
    return options[0];
  } else if (surveyPercentage >= 25 && surveyPercentage < 50) {
    return options[1];
  } else if (surveyPercentage >= 50 && surveyPercentage < 75) {
    return options[2];
  } else if (surveyPercentage >= 75 && surveyPercentage < 100) {
    return options[3];
  } else if (surveyPercentage === 100) {
    return options[4];
  } else {
    throw new Error('Invalid survey percentage');
  }
}

export function getFieldValue(id: string, values: any): string | number {
  const val = values.find((v: any) => v.field.id === id);
  return val?.value ? removeQuotes(val.value) : '';
}

export function getCurrentGEO(values: SubmissionPublicFieldFragment[]): string {
  const hasGEO = getFieldValue('M1DPA1', values);
  if (hasGEO !== 'Yes') {
    return 'none';
  }

  const score = Number(getFieldValue('Njreb7', values));
  return typeof score === 'number' && score > 8 ? 'Leadership' : 'Core';
}

export function getReclaimMultiplier(type: string): number {
  switch (type) {
    case 'Core':
      return 1.1;
    case 'Leadership':
      return 1.3;
    default:
      return 1;
  }
}

export function getIndustryBSR(name: string): number {
  switch (name) {
    case '10':
      return 0.4;
    case '4':
      return 0.2;
    default:
      return 0;
  }
}

export function getRevenueRange(
  selectedOption: string
): [number, number] | number {
  if (!selectedOption.includes('-')) {
    return Number(selectedOption.replace(/,/g, ''));
  }
  const [min, max] = selectedOption
    .split('-')
    .map((s) => Number(s.replace(/,/g, '')));
  return [min, max];
}

export function getIncrementalRevenue(
  currentGEO: string,
  recoveredSessionsFromGeo: number,
  drift: number
): number {
  if (currentGEO === 'none') {
    return 0 - drift;
  }
  return recoveredSessionsFromGeo;
}

export function getRevenueAtRisk(calculationData: any): number {
  const {
    currentGEO,
    leadershipRecoveredSessions,
    noGEORecoveredSessions,
    coreRecoveredSessions,
  } = calculationData;

  if (currentGEO === 'none') {
    return (leadershipRecoveredSessions - noGEORecoveredSessions) * 12;
  } else if (currentGEO === 'Core') {
    return (leadershipRecoveredSessions - coreRecoveredSessions) * 12;
  } else {
    return 0;
  }
}

export interface GeoData {
  revenue: number;
  BSR: number;
  aiImpactableSessions: number;
  recoveredSessionsFromGeo: number;
  reclaimMultiplier: number;
  currentGEO: string;
  drift: number;
  incrementalRevenue: number;
  coreRecoveredSessions: number;
  leadershipRecoveredSessions: number;
  estimatedRevenueAtRiskCore: number;
  noGEORecoveredSessions: number;
}

export function getGeoData(
  values: SubmissionPublicFieldFragment[],
  revenue: number
): GeoData {
  //const industryString = getFieldValue('GVkkRV', values) as IndustryKey;
  const industryString = getFieldValue('wVJ4O7', values) as IndustryKey;
  //const revenue = Number(getFieldValue('R1Wmwj', values));
  //const revenue = getFieldValue('NjGXmV', values));
  const geoMarketingBudgetPercentage = getFieldValue('wjXw47', values);
  const defaultsForIndustry = defaults[industryString];
  console.log('defaults: ', defaultsForIndustry);
  const currentBCS = getBCS(
    Number(geoMarketingBudgetPercentage),
    defaultsForIndustry.headroom
  );
  const currentGEO = getCurrentGEO(values);
  console.log('REVENUE BEFORE', revenue);

  // Cache common calculations
  const impactedRevenue = (revenue * defaultsForIndustry.BSRD) / 12;
  const aiImpactableSessions = impactedRevenue * defaultsForIndustry.AO;
  const zFactor = aiImpactableSessions * defaultsForIndustry.Z;

  const BCS = currentGEO === 'Leadership' ? currentBCS * 2 : currentBCS;
  const reclaimMultiplier = getReclaimMultiplier(currentGEO);
  const recoveredSessionsFromGeo =
    zFactor * defaultsForIndustry.RR * reclaimMultiplier * currentBCS;

  const coreRecoveredSessions =
    aiImpactableSessions *
    defaultsForIndustry.Z *
    defaultsForIndustry.CRR *
    defaultsForIndustry.DCS;
  const leadershipRecoveredSessions =
    aiImpactableSessions *
    defaultsForIndustry.Z *
    defaultsForIndustry.LRR *
    (currentBCS * 2);

  // this is for core. We need leadership and no geo as well
  const estimatedRevenueAtRiskCore =
    (leadershipRecoveredSessions - coreRecoveredSessions) * 12;
  // TODO - check if we still need estimatedRevenueAtRiskLeadership
  // const estimatedRevenueAtRiskLeadership =
  //   (coreRecoveredSessions - leadershipRecoveredSessions) * 12;

  // Calculate drift
  const drift = zFactor * defaultsForIndustry.NGD * defaultsForIndustry.RR;
  return {
    revenue,
    BSR: impactedRevenue,
    aiImpactableSessions,
    recoveredSessionsFromGeo,
    reclaimMultiplier,
    currentGEO,
    drift,
    incrementalRevenue: recoveredSessionsFromGeo,
    noGEORecoveredSessions: 0 - drift,
    //coreRecoveredSessions,
    coreRecoveredSessions: recoveredSessionsFromGeo,
    leadershipRecoveredSessions,
    estimatedRevenueAtRiskCore,
  };
}
